// src/components/ManageSubscriptionPage.jsx

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useError } from "../contexts/ErrorContext";
import {
  getPlans,
  createStripeSubscription,
  cancelStripeSubscription,
  getSubscriptionStatus,
  getCreditPacks,
  purchaseCredits,
} from "../utils/api";
import fbPixel from "../utils/fbPixel";
import CancellationFeedbackModal from "./CancellationFeedbackModal";

const UNLIMITED = 999999;

// Helper function to format feature value
const formatValue = (value) => {
  if (value === UNLIMITED) return "Unlimited";
  if (value === 0) return "No";
  return value;
};

// Helper function to format feature text
const formatFeatureText = (key, value) => {
  const featureTexts = {
    cards_per_month: (val) => `${formatValue(val)} cards per month`,
    template_limit: (val) => `${formatValue(val)} templates`,
    ai_photos_per_month: (val) => `${formatValue(val)} AI-generated photos per month`,
    collaborators_per_card: (val) => `${formatValue(val)} collaborators per card`,
    watermark: (val) => `${val ? 'Has watermark' : 'No watermark'}`,
    can_send: (val) => `${val ? 'Send cards via email' : 'Cannot send cards (upgrade required)'}`,
    can_collaborate: (val) => `${val ? 'Invite collaborators' : 'No collaborators'}`,
    custom_branding: (val) => `${val ? 'Custom branding' : 'No custom branding'}`,
    can_schedule: (val) => `${val ? 'Can schedule cards' : 'No card scheduling'}`,
    advanced_customization: (val) => `${val ? 'Advanced customization' : 'Basic customization'}`,
    priority_support: (val) => `${val ? 'Priority support' : 'Standard support'}`,
    priority_features: (val) => `${val ? 'Early access to new features' : 'Standard feature access'}`
  };

  return featureTexts[key]?.(value) || '';
};

export default function ManageSubscriptionPage() {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [purchasingCredits, setPurchasingCredits] = useState(false);
  const { showError, showSuccess } = useError();
  const navigate = useNavigate();
  const location = useLocation();
  const purchaseTrackedRef = useRef(false);
  const creditsTrackedRef = useRef(false);

  // Track Purchase event when returning from Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const checkoutSuccess = urlParams.get('checkout') === 'success' || 
                           urlParams.get('session_id') !== null;
    const creditsSuccess = urlParams.get('credits') === 'success';
    
    // Track subscription purchase
    if (checkoutSuccess && !purchaseTrackedRef.current) {
      purchaseTrackedRef.current = true;
      
      // Get subscription details for tracking
      getSubscriptionStatus().then(({ data }) => {
        if (data?.active && data?.plan_name) {
          const planPrice = data.subscription_data?.plan?.amount || 0;
          const currency = data.subscription_data?.plan?.currency || 'USD';
          const isTrial = data.is_trial || data.subscription_data?.status === 'trialing';
          
          // Track Purchase event for Facebook Ads conversion
          fbPixel.trackEvent('Purchase', {
            value: planPrice / 100, // Stripe amounts are in cents
            currency: currency.toUpperCase(),
            content_name: data.plan_name,
            content_type: 'subscription',
          });
          
          console.log('[FB Pixel] Purchase event tracked:', {
            plan: data.plan_name,
            value: planPrice / 100,
            currency: currency.toUpperCase()
          });
          
          // Show appropriate success message
          if (isTrial) {
            showSuccess(`🎉 Welcome to ${data.plan_name}! Your 3-day free trial has started. Enjoy all premium features!`);
          } else {
            showSuccess(`🎉 Welcome to ${data.plan_name}! Your subscription is now active.`);
          }
        }
      }).catch(err => {
        console.error('[FB Pixel] Failed to track purchase:', err);
      });
      
      // Clean up URL params
      window.history.replaceState({}, '', location.pathname);
    }
    
    // Track credit pack purchase
    if (creditsSuccess && !creditsTrackedRef.current) {
      creditsTrackedRef.current = true;
      
      // Refresh subscription status to get updated credit balance
      getSubscriptionStatus().then(({ data }) => {
        setSubscription(data);
        const credits = data?.card_credits || 0;
        showSuccess(`🎉 Card credits added! You now have ${credits} credit${credits !== 1 ? 's' : ''} to use.`);
        
        // Track Purchase event for credits
        fbPixel.trackEvent('Purchase', {
          content_name: 'Card Credits',
          content_type: 'credit_pack',
        });
      }).catch(err => {
        console.error('Failed to refresh subscription status:', err);
      });
      
      // Clean up URL params
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.search, location.pathname, showSuccess]);

  useEffect(() => {
    async function load() {
      try {
        const [subsResponse, plansResponse, creditPacksResponse] = await Promise.all([
          getSubscriptionStatus(),
          getPlans(),
          getCreditPacks().catch(err => {
            console.warn("Credit packs not available:", err);
            return { data: [] };
          })
        ]);
        
        // Only redirect if user has NO subscription at all AND no credits
        // Preview tier users can stay on this page to see their status and upgrade options
        const hasNoActivePlan = !subsResponse.data?.active && 
          subsResponse.data?.plan_tier === null && 
          subsResponse.data?.plan_name === 'No Plan';
        const hasCredits = subsResponse.data?.card_credits > 0;
        
        if (hasNoActivePlan && !hasCredits) {
          setIsRedirecting(true);
          navigate("/subscribe");
          return;
        }
        
        setSubscription(subsResponse.data);
        setPlans(plansResponse.data);
        setCreditPacks(creditPacksResponse.data || []);
      } catch (err) {
        console.error(err);
        showError("Failed to load subscription status");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [navigate, showError]);

  const handleCancel = () => {
    if (subscription?.cancel_at_period_end) {
      showError("Your subscription is already set to cancel at the end of the current billing period.");
      return;
    }
    // Show feedback modal instead of window.confirm
    setShowFeedbackModal(true);
  };

  const performCancellation = async () => {
    try {
      const { data } = await cancelStripeSubscription(subscription.stripe_subscription_id);
      setSubscription(prev => ({
        ...prev,
        cancel_at_period_end: true,
        next_billing_date: data.current_period_end ? new Date(data.current_period_end * 1000).toISOString() : prev.next_billing_date,
      }));
      setShowFeedbackModal(false);
      showSuccess("Cancellation scheduled – you'll retain access until the period ends.");
    } catch (err) {
      console.error(err);
      showError("Failed to schedule cancellation");
      setShowFeedbackModal(false);
    }
  };

  const handleUpgrade = async (plan) => {
    try {
      setLoading(true);
      const price = plan.prices.find(p => p.interval === billingPeriod);
      if (!price) {
        throw new Error(`No ${billingPeriod} price found for ${plan.name}`);
      }
      
      const returnUrl = window.location.origin + "/manage?checkout=success";
      const { data } = await createStripeSubscription(price.stripe_price_id, returnUrl);
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error(err);
      showError("Failed to start upgrade process", true);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCredits = async (pack) => {
    try {
      setPurchasingCredits(true);
      const returnUrl = window.location.origin + "/manage?credits=success";
      const cancelUrl = window.location.origin + "/manage?credits=canceled";
      
      const data = await purchaseCredits(pack.pack_type, returnUrl, cancelUrl);
      
      // Track credit purchase initiation
      fbPixel.trackEvent('InitiateCheckout', {
        content_name: pack.name,
        value: pack.price_cents / 100,
        currency: 'USD',
        content_type: 'credit_pack'
      });
      
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error(err);
      showError("Failed to start credit purchase", true);
    } finally {
      setPurchasingCredits(false);
    }
  };

  // Calculate yearly savings percentage
  const calculateSavings = (plan) => {
    if (!plan.prices?.length) return 0;
    const monthlyPrice = plan.prices.find(p => p.interval === 'monthly')?.amount;
    const yearlyPrice = plan.prices.find(p => p.interval === 'yearly')?.amount;
    
    if (!monthlyPrice || !yearlyPrice) return 0;
    
    const yearlyWithMonthly = monthlyPrice * 12;
    return ((yearlyWithMonthly - yearlyPrice) / yearlyWithMonthly * 100).toFixed(0);
  };

  // Helper function to get the current price for a plan
  const getCurrentPrice = (plan) => {
    if (!plan.prices?.length) return "Free";
    const price = plan.prices.find(p => p.interval === billingPeriod);
    return price ? price.amount.toFixed(2) : "N/A";
  };

  // Get features for a plan
  const getPlanFeatures = (plan) => {
    if (!plan || !plan.features) {
      return [];
    }
    const features = [];
    Object.entries(plan.features).forEach(([key, value]) => {
      const formattedFeature = formatFeatureText(key, value);
      if (formattedFeature) {
        features.push({
          text: formattedFeature,
          available: value === true || (typeof value === 'number' && value > 0)
        });
      }
    });
    return features;
  };

  // Helper function to calculate days remaining in trial
  const getTrialDaysRemaining = (trialEnd) => {
    if (!trialEnd) return null;
    const endDate = new Date(trialEnd);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Helper function to get subscription status display
  const getSubscriptionStatusDisplay = (subscription) => {
    if (!subscription) return null;
    
    // Check user status - credits users are a distinct category from Preview users
    const hasActiveSubscription = subscription.active;
    const isCreditUser = subscription.plan_tier === 'credits' || subscription.is_credit_user;
    const hasNoPlan = !isCreditUser && (subscription.plan_tier === null || subscription.plan_name === 'No Plan' || subscription.plan_name === 'Preview');
    const isFreeUser = subscription.plan_tier === 'free' && !hasActiveSubscription;
    const hasCredits = subscription.card_credits > 0;
    
    // Credit users - show their active status (NOT Preview)
    if (isCreditUser || hasCredits) {
      return {
        text: 'Pay Per Card Active',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        icon: '💳',
        description: `You have ${subscription.card_credits} card credit${subscription.card_credits !== 1 ? 's' : ''} available. Use them to send beautiful cards anytime!`
      };
    }
    
    // New users on Preview tier with no credits - show upgrade prompt
    if (hasNoPlan) {
      return {
        text: 'Preview Mode',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        icon: '🎨',
        description: 'You\'re on the free Preview tier. Create 1 card to try it out, then upgrade or buy credits to send it!'
      };
    }
    
    // Legacy free users who have a free plan subscription - show upgrade prompt
    if (isFreeUser) {
      return {
        text: 'Free Tier',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        icon: '🆓',
        description: 'You\'re on the free tier with limited features. Upgrade below to unlock premium features!'
      };
    }
    
    const status = subscription.subscription_data?.status || 'active';
    const cancelAtPeriodEnd = subscription.cancel_at_period_end;
    const isTrial = subscription.is_trial || status === 'trialing';
    const trialEnd = subscription.trial_end;
    
    // Handle trial status
    if (isTrial || status === 'trialing') {
      const daysRemaining = getTrialDaysRemaining(trialEnd);
      return {
        text: 'Free Trial',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        icon: '🎉',
        description: daysRemaining !== null 
          ? `You have ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left in your free trial. All premium features are available!`
          : 'You are currently on a free trial. All premium features are available!'
      };
    }
    
    switch (status) {
      case 'active':
        if (cancelAtPeriodEnd) {
          return {
            text: 'Active (Cancelling)',
            color: 'text-orange-600',
            bgColor: 'bg-orange-50',
            borderColor: 'border-orange-200',
            icon: '⏰',
            description: 'Your subscription will end at the current billing period.'
          };
        }
        return {
          text: 'Active',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          icon: '✅',
          description: 'Your subscription is active and all features are available.'
        };
      case 'past_due':
        return {
          text: 'Past Due',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          icon: '⚠️',
          description: 'Payment failed. Please update your payment method to continue service.'
        };
      case 'unpaid':
        return {
          text: 'Unpaid',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          icon: '❌',
          description: 'Multiple payment failures. Service may be limited until payment is resolved.'
        };
      case 'canceled':
        return {
          text: 'Canceled',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          icon: '🚫',
          description: 'Your subscription has been canceled.'
        };
      default:
        return {
          text: status.charAt(0).toUpperCase() + status.slice(1),
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          icon: 'ℹ️',
          description: `Subscription status: ${status}`
        };
    }
  };

  if (loading || isRedirecting) return (
    <div className="max-w-3xl mx-auto py-12 px-4">
      <div className="text-center">{isRedirecting ? 'Redirecting...' : 'Loading subscription details...'}</div>
    </div>
  );

  // Find current plan details
  // Credit users have plan_tier === 'credits' and should be treated differently from Preview
  const isCreditUser = subscription?.plan_tier === 'credits' || subscription?.is_credit_user;
  const hasNoPlan = !isCreditUser && (
    subscription?.plan_tier === null || 
    subscription?.plan_tier === 'preview' || 
    subscription?.plan_name === 'No Plan' || 
    subscription?.plan_name === 'Preview'
  );
  const currentPlan = (hasNoPlan || isCreditUser)
    ? null 
    : (plans.find(p => p.features.name === subscription?.plan_name) || plans[0]);
  
  // For Preview tier users, show what they have and what requires upgrade
  const previewFeatures = [
    { text: 'Unlimited card creation', available: true },
    { text: 'All templates', available: true },
    { text: 'Cannot send cards (upgrade required)', available: false },
    { text: 'No collaborators', available: false },
    { text: 'Has watermark', available: false },
    { text: 'No AI-generated photos', available: false },
    { text: 'No card scheduling', available: false },
    { text: 'Basic customization', available: false },
  ];
  
  // For users with credits (pay-per-card), show what they can do
  const creditUserFeatures = [
    { text: 'Unlimited card creation', available: true },
    { text: 'All templates', available: true },
    { text: 'Send cards using credits', available: true },
    { text: 'No watermark on sent cards', available: true },
    { text: 'AI-generated photos (uses credits)', available: true },
    { text: 'Credits never expire', available: true },
    { text: 'Invite collaborators', available: true },
    { text: 'Schedule cards for later', available: true },
  ];
  
  const currentFeatures = isCreditUser 
    ? creditUserFeatures 
    : hasNoPlan 
      ? previewFeatures
      : getPlanFeatures(currentPlan);

  // Derived flags – handle API shape differences
  const cancelAtPeriodEnd = subscription?.cancel_at_period_end || subscription?.subscription_data?.cancel_at_period_end;
  const nextBillingDate = subscription?.next_billing_date || subscription?.subscription_data?.next_billing_date;

  return (
    <div className="max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Manage Plan & Credits</h1>
      </div>

      {/* Current Plan Section */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
        <div className="px-6 py-8 sm:p-10">
          <div className="flex justify-center">
            <span className={`inline-flex px-4 py-1 rounded-full text-sm font-semibold tracking-wide uppercase ${
              subscription?.plan_tier === 'credits' || subscription?.is_credit_user
                ? 'bg-emerald-100 text-emerald-600' 
                : subscription?.plan_tier === 'pro' || subscription?.plan_tier === 'premium'
                  ? 'bg-purple-100 text-purple-600'
                  : 'bg-slate-100 text-slate-600'
            }`}>
              {subscription?.plan_tier === 'pro' ? '👑 Pro Plan' : 
               subscription?.plan_tier === 'premium' ? '⭐️ Premium Plan' : 
               subscription?.plan_tier === 'credits' || subscription?.is_credit_user ? '💳 Pay Per Card' :
               subscription?.plan_tier === 'free' && subscription?.active ? '🎯 Basic Plan' :
               '🎨 Preview'}
            </span>
          </div>
          
          {/* Enhanced Status Display */}
          {subscription && (() => {
            const statusDisplay = getSubscriptionStatusDisplay(subscription);
            if (statusDisplay) {
              return (
                <div className={`mt-6 p-4 rounded-lg border ${statusDisplay.bgColor} ${statusDisplay.borderColor}`}>
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{statusDisplay.icon}</span>
                    <div>
                      <h3 className={`text-lg font-semibold ${statusDisplay.color}`}>
                        {statusDisplay.text}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {statusDisplay.description}
                      </p>
                      {statusDisplay.text.includes('Past Due') && (
                        <div className="mt-2">
                          <a
                            href="https://billing.stripe.com/p/login"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 underline"
                          >
                            → Update Payment Method
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}
          
          <div className="mt-4">
            <h2 className="text-2xl font-bold text-center mb-6">
              {isCreditUser 
                ? 'Your Benefits' 
                : hasNoPlan 
                  ? 'What You\'re Missing' 
                  : 'Current Plan Benefits'}
            </h2>
            <ul className="space-y-4">
              {currentFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="flex-shrink-0">
                    {feature.available ? (
                      <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <p className="ml-3 text-base text-gray-700">{feature.text}</p>
                </li>
              ))}
            </ul>
          </div>

          {subscription?.stripe_subscription_id && !cancelAtPeriodEnd && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700"
              >
                Cancel Subscription
              </button>
            </div>
          )}
          {cancelAtPeriodEnd && (
            <div className="mt-8 text-center text-sm text-gray-600">
              {nextBillingDate
                ? `Your plan will end on ${new Date(nextBillingDate).toLocaleDateString()}.`
                : "Your plan will remain active until the end of the current billing period."}
            </div>
          )}
        </div>
      </div>

      {/* Card Credits Section */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
        <div className="px-6 py-8 sm:p-10">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">💳</span>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Card Credits</h2>
                <p className="text-sm text-gray-500">Pay-per-card option for occasional sending</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-gray-900">
                {subscription?.card_credits || 0}
              </div>
              <div className="text-sm text-gray-500">
                credit{subscription?.card_credits !== 1 ? 's' : ''} available
              </div>
            </div>
          </div>
          
          {/* Credit Balance Bar */}
          {subscription?.card_credits > 0 && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">✨</span>
                <div>
                  <p className="font-semibold text-emerald-800">
                    You have {subscription.card_credits} card credit{subscription.card_credits !== 1 ? 's' : ''}!
                  </p>
                  <p className="text-sm text-emerald-700">
                    Use them to send cards anytime. Credits never expire.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Buy Credits Section */}
          <div className="grid md:grid-cols-2 gap-4">
            {creditPacks.map((pack) => (
              <div 
                key={pack.pack_type}
                className={`border rounded-xl p-4 transition-all hover:shadow-md ${
                  pack.pack_type === 'bundle_10' 
                    ? 'border-emerald-300 bg-emerald-50/50' 
                    : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{pack.name}</h3>
                    <p className="text-sm text-gray-500">{pack.description}</p>
                  </div>
                  {pack.savings && (
                    <span className="text-xs font-semibold px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                      {pack.savings}
                    </span>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-gray-900">{pack.price_display}</span>
                  <button
                    onClick={() => handleBuyCredits(pack)}
                    disabled={purchasingCredits}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      pack.pack_type === 'bundle_10'
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-gray-800 text-white hover:bg-gray-700'
                    }`}
                  >
                    {purchasingCredits ? 'Processing...' : `Buy ${pack.credits} Card${pack.credits > 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* How Credits Work */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-600 text-center">
              <span className="font-medium">How it works:</span> Credits are only used when you send a card or invite collaborators. 
              Generate unlimited AI images while designing — you won't be charged until you finalize.
            </p>
          </div>
        </div>
      </div>

      {/* Upgrade Section (show for free plan or Preview tier users) */}
      {(subscription?.plan_tier === 'free' || subscription?.plan_tier === null || subscription?.plan_tier === 'preview' || subscription?.plan_name === 'No Plan' || subscription?.plan_name === 'Preview') && (
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Upgrade Your Plan</h2>
            <p className="mt-4 text-lg text-gray-600">Get access to more features and benefits</p>
            
            {/* Billing Period Toggle */}
            <div className="mt-6 inline-flex items-center bg-gray-100 rounded-lg p-1">
              <button
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  billingPeriod === 'monthly'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  billingPeriod === 'yearly'
                    ? 'bg-white shadow-sm text-gray-900'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                onClick={() => setBillingPeriod('yearly')}
              >
                Yearly
              </button>
            </div>
          </div>

          {/* Premium and Pro Plan Cards */}
          <div className="grid md:grid-cols-2 gap-8">
            {plans.slice(1).map((plan, index) => {
              const features = getPlanFeatures(plan);
              const emoji = ["⭐️", "👑"][index];
              
              return (
                <div 
                  key={plan.name}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow"
                >
                  <div className="px-6 py-8 sm:p-10 sm:pb-6">
                    <div className="flex justify-center">
                      <span className="inline-flex px-4 py-1 rounded-full text-sm font-semibold tracking-wide uppercase bg-purple-100 text-purple-600">
                        {emoji} {plan.name}
                      </span>
                    </div>
                    <div className="mt-4 flex justify-center text-6xl font-extrabold text-gray-900">
                      <span className="ml-1 mr-3 text-xl font-medium text-gray-500 leading-loose">$</span>
                      <span>{getCurrentPrice(plan)}</span>
                      <span className="ml-1 text-xl font-medium text-gray-500 leading-loose">
                        /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    </div>
                    {billingPeriod === 'yearly' && (
                      <p className="mt-2 text-center text-sm text-green-600 font-semibold">
                        Save {calculateSavings(plan)}%
                      </p>
                    )}
                  </div>
                  <div className="px-6 pt-6 pb-8 bg-gray-50 sm:px-10 sm:py-10">
                    <ul className="space-y-4">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className="flex-shrink-0">
                            {feature.available ? (
                              <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </div>
                          <p className="ml-3 text-base text-gray-700">{feature.text}</p>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8">
                      {/* Free trial badge */}
                      <div className="mb-3 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                          🎁 3-day free trial
                        </span>
                      </div>
                      <button
                        onClick={() => handleUpgrade(plan)}
                        className="w-full px-6 py-3 text-base font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md shadow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition"
                        disabled={loading}
                      >
                        {loading ? "Please wait..." : "Start Free Trial"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancellation Feedback Modal */}
      {showFeedbackModal && (
        <CancellationFeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          onConfirmCancel={performCancellation}
        />
      )}
    </div>
  );
}
